import React, { useCallback, useEffect, useRef, useState } from 'react';
import cn from 'classnames';

import styles from './styles.module.scss';
import { IPropsAdvertisement } from './types';
import { AdvertisementContent } from './AdvertisementContent';
import { BrowserStorage, Instance } from '@app/const/consts';
import { getDataFromLocalStorage, getDfLogoSrc } from '@app/utils/utils';
import { useSelector } from 'react-redux';
import { IApplicationState } from '@app/store/types/commonTypes';
import logo from '../../../img/new/logo.svg';
import { AuthBrandFull } from '@infologistics/frontend-libraries';
import { useTranslation } from 'react-i18next';
import { ADVERTISEMENTS, ADVERTISEMENTS_SAME_SLIDE_IDX } from './consts';

const imgArrow = `/assets/img/login/arrow.svg`;

const SLIDE_INTERVAL = 15000;
const SLIDE_DURATION = 500;

const getRandomNumber = (arrayLength: number) => {
  return Math.floor(Math.random() * arrayLength);
};

const getRandomOrder = (arrayLength: number, prevFirst?: number[]) => {
  let arraySrc = Array.from({ length: arrayLength }, (_, i) => i);
  const arrayFirst = arraySrc.filter((val) => !prevFirst?.includes(val));

  const firstElem = getRandomNumber(arrayFirst.length);
  let arrayFinal: number[] = [arrayFirst[firstElem]];
  arraySrc = arraySrc.filter((val) => val !== arrayFirst[firstElem]);

  while (arraySrc.length) {
    const elem = getRandomNumber(arraySrc.length);
    arrayFinal = [...arrayFinal, ...[arraySrc[elem]]];
    arraySrc.splice(elem, 1);
  }

  if (ADVERTISEMENTS_SAME_SLIDE_IDX.length) {
    if (
      ADVERTISEMENTS_SAME_SLIDE_IDX.includes(arrayFinal[0]) &&
      ADVERTISEMENTS_SAME_SLIDE_IDX.includes(arrayFinal[arrayFinal.length - 1])
    ) {
      const save = arrayFinal[0];
      arrayFinal[0] = arrayFinal[1];
      arrayFinal[1] = save;
    } else {
      for (let i = 0; i < arrayFinal.length - 1; i++) {
        if (ADVERTISEMENTS_SAME_SLIDE_IDX.includes(arrayFinal[i]) && ADVERTISEMENTS_SAME_SLIDE_IDX.includes(arrayFinal[i + 1])) {
          const changeIdx = i === arrayFinal.length - 2 ? i : i + 2;

          const save = arrayFinal[changeIdx - 1];
          arrayFinal[changeIdx - 1] = arrayFinal[changeIdx];
          arrayFinal[changeIdx] = save;
        }
      }
    }
  }

  return arrayFinal;
};

export const Advertisement = ({ copyright, disableAdvertisement, horizontalView }: IPropsAdvertisement) => {
  const [order, setOrder] = useState(ADVERTISEMENTS.map((_, idx) => idx));
  const [currentIdx, setCurrentIdx] = useState(0);
  const [pendingIdx, setPendingIdx] = useState<number | null>(null);
  const [direction, setDirection] = useState<'next' | 'prev' | null>(null);

  const refInterval = useRef<NodeJS.Timeout | null>(null);
  const currentIdxRef = useRef(currentIdx);
  const directionRef = useRef<'next' | 'prev' | null>(direction);

  const instance = useSelector((state: IApplicationState) => state.utils.instance);
  const instanceRU = instance === Instance.RUSSIAN;

  const logoSrc = getDfLogoSrc(logo, true);

  const { t } = useTranslation();

  const getNewIdx = useCallback((idx: number) => {
    if (idx === ADVERTISEMENTS.length) {
      return 0;
    }

    if (idx < 0) {
      return ADVERTISEMENTS.length - 1;
    }

    return idx;
  }, []);

  useEffect(() => {
    currentIdxRef.current = currentIdx;
  }, [currentIdx]);

  useEffect(() => {
    directionRef.current = direction;
  }, [direction]);

  useEffect(() => {
    const prevFirstLS = getDataFromLocalStorage(BrowserStorage.ADVERTISEMENT_FIRST);
    const prevFirst = prevFirstLS ? Number(JSON.parse(prevFirstLS)) : undefined;
    const randomOrder = getRandomOrder(
      ADVERTISEMENTS.length,
      prevFirst !== undefined
        ? ADVERTISEMENTS_SAME_SLIDE_IDX.includes(prevFirst)
          ? ADVERTISEMENTS_SAME_SLIDE_IDX
          : [prevFirst]
        : undefined,
    );
    setOrder(randomOrder);
    window.localStorage.setItem(BrowserStorage.ADVERTISEMENT_FIRST, JSON.stringify(randomOrder[0]));
  }, []);

  const stopInterval = useCallback(() => {
    if (refInterval.current) {
      clearInterval(refInterval.current);
      refInterval.current = null;
    }
  }, []);

  const getDirectionBetween = useCallback((from: number, to: number): 'next' | 'prev' => {
    if (from === to) {
      return 'next';
    }

    const lastIdx = ADVERTISEMENTS.length - 1;

    if (from === 0 && to === lastIdx) {
      return 'prev';
    }

    if (from === lastIdx && to === 0) {
      return 'next';
    }

    return to > from ? 'next' : 'prev';
  }, []);

  const startTransition = useCallback(
    (targetIdx: number, forcedDirection?: 'next' | 'prev') => {
      if (directionRef.current) {
        return false;
      }

      const normalizedIdx = getNewIdx(targetIdx);
      const fromIdx = currentIdxRef.current;

      if (normalizedIdx === fromIdx) {
        return false;
      }

      stopInterval();

      const transitionDirection = forcedDirection ?? getDirectionBetween(fromIdx, normalizedIdx);

      directionRef.current = transitionDirection;
      setDirection(transitionDirection);
      setPendingIdx(normalizedIdx);

      return true;
    },
    [getDirectionBetween, getNewIdx, stopInterval],
  );

  const startInterval = useCallback(() => {
    stopInterval();

    refInterval.current = setInterval(() => {
      if (directionRef.current) {
        return;
      }

      const nextIdx = getNewIdx(currentIdxRef.current + 1);
      startTransition(nextIdx, 'next');
    }, SLIDE_INTERVAL);
  }, [getNewIdx, startTransition, stopInterval]);

  useEffect(() => {
    startInterval();

    return () => {
      stopInterval();
    };
  }, [startInterval, stopInterval]);

  useEffect(() => {
    if (!direction || pendingIdx === null) {
      return;
    }

    const timeout = setTimeout(() => {
      currentIdxRef.current = pendingIdx;
      directionRef.current = null;

      setCurrentIdx(pendingIdx);
      setPendingIdx(null);
      setDirection(null);

      startInterval();
    }, SLIDE_DURATION);

    return () => clearTimeout(timeout);
  }, [direction, pendingIdx, startInterval]);

  const handleSwitch = useCallback(
    (newIdx: number, forcedDirection?: 'next' | 'prev') => {
      const started = startTransition(newIdx, forcedDirection);

      if (!started && !directionRef.current) {
        startInterval();
      }
    },
    [startInterval, startTransition],
  );

  const handleSwitchImage = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, idx: number, forcedDirection: 'next' | 'prev') => {
    e.preventDefault();
    handleSwitch(idx, forcedDirection);
  };

  const prevIdx = getNewIdx(currentIdx - 1);
  const nextIdx = getNewIdx(currentIdx + 1);
  const leftIncomingIdx =
    direction === 'next' ? currentIdx : direction === 'prev' && pendingIdx !== null ? getNewIdx(pendingIdx - 1) : null;
  const rightIncomingIdx =
    direction === 'next' && pendingIdx !== null ? getNewIdx(pendingIdx + 1) : direction === 'prev' ? currentIdx : null;
  const isAnimating = direction !== null;
  const disableAdvertisementFull = !instanceRU || disableAdvertisement;

  return (
    <div className={cn(styles.container, { [styles.container_ru]: !disableAdvertisementFull })}>
      {disableAdvertisementFull && (
        <AuthBrandFull
          logo={logoSrc}
          logoAlt='docuforce'
          description={t('auth:description')}
          copyright={copyright}
          stylesBrandContainer={{
            width: '100%',
            height: '100%',
          }}
        />
      )}
      {!disableAdvertisementFull && (
        <>
          <div className={styles.carousel}>
            <div
              key={`current-${order[currentIdx]}`}
              className={cn(
                styles.carousel__item,
                styles.carousel__item_current,
                direction === 'next' && styles.carousel__item_current_next,
                direction === 'prev' && styles.carousel__item_current_prev,
              )}
            >
              <AdvertisementContent item={ADVERTISEMENTS[order[currentIdx]]} disableClick={isAnimating} horizontalView={horizontalView} />
            </div>

            {direction && pendingIdx !== null && (
              <div
                key={`incoming-${order[pendingIdx]}-${direction}`}
                className={cn(
                  styles.carousel__item,
                  styles.carousel__item_incoming,
                  direction === 'next' ? styles.carousel__item_incoming_next : styles.carousel__item_incoming_prev,
                )}
              >
                <AdvertisementContent item={ADVERTISEMENTS[order[pendingIdx]]} disableClick horizontalView={horizontalView} />
              </div>
            )}
          </div>

          <div className={styles.shadow__left} onClick={(e) => handleSwitchImage(e, currentIdx - 1, 'prev')}>
            <div
              key={`shadow-left-current-${order[prevIdx]}-${direction ?? 'idle'}`}
              className={cn(
                styles.shadow__item,
                styles.shadow__item_current,
                direction === 'prev' && styles.shadow__item_current_left_to_center,
                direction === 'next' && styles.shadow__item_current_left_to_outer,
              )}
            >
              <AdvertisementContent item={ADVERTISEMENTS[order[prevIdx]]} disableClick horizontalView={horizontalView} />
            </div>

            {direction && leftIncomingIdx !== null && (
              <div
                key={`shadow-left-incoming-${order[leftIncomingIdx]}-${direction}`}
                className={cn(
                  styles.shadow__item,
                  styles.shadow__item_incoming,
                  direction === 'next' ? styles.shadow__item_incoming_left_from_center : styles.shadow__item_incoming_left_from_outer,
                )}
              >
                <AdvertisementContent item={ADVERTISEMENTS[order[leftIncomingIdx]]} disableClick horizontalView={horizontalView} />
              </div>
            )}
          </div>

          <div className={styles.shadow__right} onClick={(e) => handleSwitchImage(e, currentIdx + 1, 'next')}>
            <div
              key={`shadow-right-current-${order[nextIdx]}-${direction ?? 'idle'}`}
              className={cn(
                styles.shadow__item,
                styles.shadow__item_current,
                direction === 'next' && styles.shadow__item_current_right_to_center,
                direction === 'prev' && styles.shadow__item_current_right_to_outer,
              )}
            >
              <AdvertisementContent item={ADVERTISEMENTS[order[nextIdx]]} disableClick horizontalView={horizontalView} />
            </div>

            {direction && rightIncomingIdx !== null && (
              <div
                key={`shadow-right-incoming-${order[rightIncomingIdx]}-${direction}`}
                className={cn(
                  styles.shadow__item,
                  styles.shadow__item_incoming,
                  direction === 'next' ? styles.shadow__item_incoming_right_from_outer : styles.shadow__item_incoming_right_from_center,
                )}
              >
                <AdvertisementContent item={ADVERTISEMENTS[order[rightIncomingIdx]]} disableClick horizontalView={horizontalView} />
              </div>
            )}
          </div>

          <div className={styles.switch}>
            <button className={styles.btn} onClick={() => handleSwitch(currentIdx - 1, 'prev')}>
              <img src={imgArrow} className={styles.switch__arrow} />
            </button>

            <div className={styles.switch__dots}>
              {ADVERTISEMENTS.map((_val, idx) => (
                <button
                  key={idx}
                  className={cn(idx === currentIdx ? styles.dots__item_active : styles.dots__item, styles.btn)}
                  onClick={() => handleSwitch(idx, idx > currentIdx ? 'next' : 'prev')}
                />
              ))}
            </div>

            <button className={styles.btn} onClick={() => handleSwitch(currentIdx + 1, 'next')}>
              <img src={imgArrow} className={styles.switch__arrow_right} />
            </button>
          </div>
        </>
      )}
    </div>
  );
};
